import React, { useMemo, useState, useEffect } from 'react';
import './Pricing.css';
import { planOrder, pricingTranslations } from '../../config/pricingTranslations';
import { useLanguage } from '../../context/LanguageContext';
import { createCheckoutSession, getSubscriptionStatus, getSubscriptionPlans } from '../../services/subscriptionService';
import { validateReferralCode } from '../../services/epService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const Pricing: React.FC = () => {
  const { language } = useLanguage();
  const translation = pricingTranslations[language];
  const isRtl = language === 'ar';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('free');
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [backendPlans, setBackendPlans] = useState<any>(null);

  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [referralPartner, setReferralPartner] = useState('');
  const [discountPercent, setDiscountPercent] = useState<number | undefined>(undefined);
  const [referralType, setReferralType] = useState<'referral' | 'promo' | undefined>(undefined);
  const normalizedReferralCode = referralCode.trim().toUpperCase();
  const isPromoCode = referralStatus === 'valid' && referralType === 'promo';
  const canApplyPaidPlanDiscount = referralStatus === 'valid' && !isPromoCode;

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced referral code validation
  useEffect(() => {
    if (!referralCode.trim()) {
      setReferralStatus('idle');
      setReferralPartner('');
      setDiscountPercent(undefined);
      setReferralType(undefined);
      return;
    }
    const timer = setTimeout(async () => {
      setReferralStatus('validating');
      try {
        const result = await validateReferralCode(referralCode.trim());
        if (result.valid) {
          setReferralStatus('valid');
          setReferralPartner(result.epName || 'Partner');
          setDiscountPercent(result.discountPercent);
          setReferralType(result.type || 'referral');
        } else {
          setReferralStatus('invalid');
          setReferralPartner('');
          setDiscountPercent(undefined);
          setReferralType(undefined);
        }
      } catch {
        setReferralStatus('invalid');
        setReferralPartner('');
        setDiscountPercent(undefined);
        setReferralType(undefined);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [referralCode]);

  const fetchData = async () => {
    try {
      const plansData = await getSubscriptionPlans();
      setBackendPlans(plansData);

      // We can fetch status in parallel but failures shouldn't block plans showing
      try {
        const statusData = await getSubscriptionStatus();
        if (statusData.hasSubscription && statusData.status === 'active') {
          setSubscriptionStatus('active');
        }
      } catch (e) {
        // user might not be logged in, ignore status fetch error
      }

    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load pricing information');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async (planKey: string) => {
    if (subscriptionStatus === 'active') {
      navigate('/subscription-management');
      return;
    }

    setLoading(true);
    try {
      let backendKey = '';
      if (planKey === 'monthly') backendKey = 'MONTHLY';
      else if (planKey === 'halfYear') backendKey = 'SIX_MONTH';
      else if (planKey === 'yearly') backendKey = 'YEARLY';

      const priceId = backendPlans?.[backendKey]?.priceId;

      if (!priceId || priceId.includes('placeholder')) {
        toast.error('Configuration Error: Price ID not set. Please contact support.');
        return;
      }

      const validCode = canApplyPaidPlanDiscount ? normalizedReferralCode : undefined;
      const session = await createCheckoutSession(priceId, validCode);
      if (session && session.url) {
        window.location.href = session.url;
      } else {
        toast.error('Failed to start checkout session');
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.message || 'Error initiating checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleActivatePromo = async () => {
    if (subscriptionStatus === 'active') {
      navigate('/subscription-management');
      return;
    }

    const priceId = backendPlans?.MONTHLY?.priceId;

    if (!priceId || priceId.includes('placeholder')) {
      toast.error('Configuration Error: Price ID not set. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      const session = await createCheckoutSession(priceId, normalizedReferralCode);
      if (session.subscriptionActivated) {
        toast.success(session.message || `${normalizedReferralCode} plan activated for 1 month.`);
        await fetchData();
        return;
      }

      if (session.url) {
        window.location.href = session.url;
      } else {
        toast.error(`Failed to activate ${normalizedReferralCode} plan.`);
      }
    } catch (error: any) {
      console.error('Promo activation error:', error);
      toast.error(error.response?.data?.message || `Failed to activate ${normalizedReferralCode} plan.`);
    } finally {
      setLoading(false);
    }
  };

  const plans = useMemo(() => {
    if (!backendPlans) return [];

    return planOrder.map((key) => {
      let backendKey = '';
      if (key === 'monthly') backendKey = 'MONTHLY';
      else if (key === 'halfYear') backendKey = 'SIX_MONTH';
      else if (key === 'yearly') backendKey = 'YEARLY';

      const backendPlan = backendPlans[backendKey];

      return {
        key,
        price: backendPlan ? backendPlan.price : 0,
        currencySymbol: backendPlan ? backendPlan.currencySymbol : '$',
        ...translation.plans[key],
      };
    });
  }, [translation, backendPlans]);

  const visiblePlans = plans; // Show all plans

  if (checkingStatus) {
    return <div className="pricing-container"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="pricing-container" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="pricing-header">
        <h1>{translation.heading}</h1>
        {subscriptionStatus === 'active' && (
          <p className="active-sub-badge">You have an active subscription ✅</p>
        )}
      </div>

      {/* ── Referral Code – Professional Flow ── */}
      <div className={`referral-box${referralStatus === 'valid' ? ' referral-box--applied' : ''}`}>

        {/* Top row: icon + title + toggle */}
        <div className="referral-box__header">
          <span className="referral-box__gift-icon">🎁</span>
          <div className="referral-box__title-group">
            <p className="referral-box__title">Have a referral code?</p>
            <p className="referral-box__subtitle">Get an exclusive discount from your partner</p>
          </div>
          {referralStatus === 'valid' && (
            <span className="referral-box__badge">✓ APPLIED</span>
          )}
        </div>

        {/* Input row */}
        <div className="referral-box__input-row">
          <div className={`referral-box__input-wrap${referralStatus === 'valid' ? ' is-valid' :
            referralStatus === 'invalid' ? ' is-invalid' :
              referralStatus === 'validating' ? ' is-checking' : ''
            }`}>
            <span className="referral-box__input-prefix">#</span>
            <input
              type="text"
              className="referral-box__input"
              placeholder="Enter code  e.g. EP-ABCD1234"
              value={referralCode}
              onChange={(e) => {
                setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''));
              }}
              maxLength={20}
              spellCheck={false}
              autoComplete="off"
            />
            {/* State indicators inside input */}
            {referralStatus === 'validating' && (
              <span className="referral-box__spinner" title="Validating…" />
            )}
            {referralStatus === 'valid' && (
              <span className="referral-box__check">✓</span>
            )}
            {referralStatus === 'invalid' && referralCode.trim().length > 0 && (
              <span className="referral-box__cross">✕</span>
            )}
          </div>

          {/* Clear button */}
          {referralCode.length > 0 && referralStatus !== 'validating' && (
            <button
              className="referral-box__clear-btn"
              onClick={() => { setReferralCode(''); setReferralStatus('idle'); setReferralPartner(''); setDiscountPercent(undefined); setReferralType(undefined); }}
              title="Clear code"
            >✕</button>
          )}
        </div>

        {/* Status messages */}
        {referralStatus === 'validating' && (
          <p className="referral-box__msg referral-box__msg--checking">
            <span className="referral-box__dot-pulse" />
            Verifying your referral code…
          </p>
        )}

        {referralStatus === 'valid' && (
          <div className="referral-box__success-chip">
            <span className="referral-box__success-icon">🎉</span>
            <div>
              <span className="referral-box__success-title">
                {isPromoCode ? `${normalizedReferralCode} Unlocked!` : 'Referral Applied!'} {!isPromoCode && discountPercent ? <strong className="referral-discount-badge">{discountPercent}% OFF</strong> : ''}
              </span>
              <span className="referral-box__success-sub">
                {isPromoCode
                  ? 'Separate 1-month promo plan available below'
                  : <>Referred by <strong>{referralPartner}</strong> · Discount will be applied at checkout</>}
              </span>
            </div>
          </div>
        )}

        {referralStatus === 'invalid' && referralCode.trim().length > 0 && (
          <p className="referral-box__msg referral-box__msg--error">
            <span>⚠️</span> Invalid code. Please check and try again.
          </p>
        )}
      </div>

      {isPromoCode && subscriptionStatus !== 'active' && (
        <div className="matiyari-promo-card">
          <div>
            <span className="matiyari-promo-label">{normalizedReferralCode} Promo Plan</span>
            <h2>1 Month Free Access</h2>
            <p className="matiyari-promo-price">₹0 <span>/ month</span></p>
            <ul>
              <li>Included for 1 month</li>
              <li>VAD: This feature is not available on this subscription.</li>
              <li>Mock Test: 1 mock test per day on this subscription.</li>
            </ul>
          </div>
          <button className="matiyari-promo-button" onClick={handleActivatePromo} disabled={loading}>
            {loading ? 'Activating...' : `Activate ${normalizedReferralCode} Plan`}
          </button>
        </div>
      )}

      <div className="pricing-grid">
        {visiblePlans.map((plan) => (
          <div key={plan.key} className="pricing-card featured">
            <h2>{plan.name}</h2>
            <div className="price">
              <span className="currency">{plan.currencySymbol}</span>
              {canApplyPaidPlanDiscount && discountPercent ? (
                <>
                  <span className="amount original-price" style={{ textDecoration: 'line-through', fontSize: '0.5em', opacity: 0.5, marginRight: '8px' }}>
                    {plan.price}
                  </span>
                  <span className="amount">{Math.round(plan.price * (1 - discountPercent / 100))}</span>
                </>
              ) : (
                <span className="amount">{plan.price}</span>
              )}
              <span className="period">/{plan.period}</span>
            </div>
            <ul className="features">
              {plan.features.map((feature, idx) => (
                <li key={`${plan.key}-${idx}`}>{feature}</li>
              ))}
            </ul>
            <button
              className={`cta-button ${subscriptionStatus === 'active' ? 'secondary' : ''}`}
              onClick={() => handleSubscribe(plan.key)}
              disabled={loading}
            >
              {loading ? 'Processing...' : subscriptionStatus === 'active' ? 'Manage Subscription' : translation.choosePlan}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Pricing;
